import { useId } from "react";
import { Info } from "lucide-react";
import { Text } from "@ferrivia/ui";
import type { AnswerValue, ItemFieldMap, QuestionDefinition } from "@ferrivia/api-contract";
import { WhyWeAsk } from "../../onboarding/components/WhyWeAsk";
import { useQuestionnaireCopy } from "@ferrivia/questionnaire-ui";
import { BooleanQuestion } from "./BooleanQuestion";
import { SingleChoiceQuestion } from "./SingleChoiceQuestion";
import { MultiChoiceQuestion } from "./MultiChoiceQuestion";
import { SearchableMultiChoice } from "./SearchableMultiChoice";
import { TextQuestion } from "./TextQuestion";
import { NumberQuestion } from "./NumberQuestion";
import { DateQuestion } from "./DateQuestion";
import { RepeatingGroup } from "./RepeatingGroup";
import { OptionGuideSheet } from "./OptionGuideSheet";
import s from "./QuestionBlock.module.css";

export interface QuestionBlockProps {
  question: QuestionDefinition;
  value: AnswerValue | undefined;
  onChange: (value: AnswerValue) => void;
  errorText?: string;
  /** Shown above the control while the value is an untouched prefill. */
  prefilledHint?: string;
  /** Only read when `question.repeating` -- a repeating-group question has
      no single `value`/`onChange` of its own, it has a list of items each
      with their own fields. See `RepeatingGroup`'s own doc comment for why
      `itemIndexes` is carried separately from `items`. */
  items: readonly ItemFieldMap[];
  itemIndexes: readonly number[];
  itemInvalidKeys: ReadonlySet<string>;
  /** `null` opens the item editor on a new item. */
  onOpenEditor(itemIndex: number | null): void;
  /** Where a question's option guide sheet portals to. */
  sheetContainer?: HTMLElement | null;
}

/** One question, from label to control: dispatches on `question.kind`,
    the fixed set QuestionBlock's own dispatch covers being the six single-
    question answer kinds. A repeating-group question (`iron_supplements`,
    `other_supplement_items`, `iron_infusion_items`) dispatches to
    `RepeatingGroup` instead. */
export function QuestionBlock({
  question,
  value,
  onChange,
  errorText,
  prefilledHint,
  items,
  itemIndexes,
  itemInvalidKeys,
  onOpenEditor,
  sheetContainer = null,
}: QuestionBlockProps) {
  const copy = useQuestionnaireCopy();
  const labelId = useId();

  if (question.repeating) {
    return (
      <RepeatingGroup
        group={question}
        items={items}
        itemIndexes={itemIndexes}
        invalidKeys={itemInvalidKeys}
        onOpenEditor={onOpenEditor}
      />
    );
  }

  // A `const` alias, not `question` itself: TypeScript doesn't carry the
  // `!repeating` narrowing from the guard above into a nested function
  // closure (renderControl), only into `const` bindings declared after it.
  const singleQuestion = question;
  const questionCopy = copy.questionFor(singleQuestion.key);
  const label = questionCopy?.label ?? singleQuestion.key;
  const optionLabelFor = (optionId: string) => copy.optionLabelFor(singleQuestion.key, optionId) ?? optionId;

  function renderControl() {
    switch (singleQuestion.kind) {
      case "boolean":
        return (
          <BooleanQuestion
            labelledBy={labelId}
            value={value?.kind === "boolean" ? value.value : undefined}
            onChange={(next) => onChange({ kind: "boolean", value: next })}
          />
        );
      case "choice":
        return (
          <SingleChoiceQuestion
            question={singleQuestion}
            labelledBy={labelId}
            optionLabelFor={optionLabelFor}
            value={value?.kind === "choice" ? value.value : undefined}
            onChange={(optionId) => onChange({ kind: "choice", value: optionId })}
          />
        );
      case "multi_choice": {
        const Control = singleQuestion.control === "searchable_multi_choice" ? SearchableMultiChoice : MultiChoiceQuestion;
        return (
          <Control
            question={singleQuestion}
            legend={label}
            optionLabelFor={optionLabelFor}
            value={value?.kind === "multi_choice" ? value.value : []}
            onChange={(selected) => onChange({ kind: "multi_choice", value: selected })}
          />
        );
      }
      case "text":
        return (
          <TextQuestion
            labelledBy={labelId}
            value={value?.kind === "text" ? value.value : ""}
            onChange={(next) => onChange({ kind: "text", value: next })}
            errorText={errorText}
            long={singleQuestion.control === "long_text"}
          />
        );
      case "number":
        return (
          <NumberQuestion
            question={singleQuestion}
            labelledBy={labelId}
            unit={questionCopy?.unit}
            value={value?.kind === "number" ? value.value : undefined}
            onChange={(next) => onChange({ kind: "number", value: next })}
            errorText={errorText}
          />
        );
      case "date":
        return (
          <DateQuestion
            labelledBy={labelId}
            value={value?.kind === "date" ? value.value : ""}
            onChange={(next) => onChange({ kind: "date", value: next })}
            errorText={errorText}
          />
        );
    }
  }

  return (
    <div className={s.root}>
      {/* multi_choice's fieldset/legend already carries the accessible
          name -- an <h2> above it would duplicate the label for a screen
          reader that just heard the legend. Every other kind needs the
          heading as the id its control's aria-labelledby points at. */}
      {singleQuestion.kind !== "multi_choice" ? (
        <Text as="h2" size="lg" weight="bold" id={labelId} className={s.label}>
          {label}
        </Text>
      ) : null}
      {questionCopy?.intro ? (
        <div className={s.intro}>
          <Info aria-hidden size={18} className={s.introIcon} />
          <Text as="p" size="sm" className={s.introText}>
            {questionCopy.intro}
          </Text>
        </div>
      ) : null}
      {questionCopy?.whyWeAsk ? (
        <WhyWeAsk linkText={questionCopy.whyWeAsk.linkText}>{questionCopy.whyWeAsk.text}</WhyWeAsk>
      ) : null}
      {prefilledHint ? (
        <Text as="p" size="sm" className={s.prefilledHint}>
          {prefilledHint}
        </Text>
      ) : null}
      {renderControl()}
      {questionCopy?.optionGuide ? (
        <OptionGuideSheet
          question={singleQuestion}
          guide={questionCopy.optionGuide}
          optionLabelFor={optionLabelFor}
          container={sheetContainer}
        />
      ) : null}
    </div>
  );
}
